import type { SupabaseClient } from "@supabase/supabase-js";
import { createOutboxStore } from "./outboxFactory";
import { SupabaseSyncTransport } from "./supabaseTransport";
import { SyncEngine, type SyncRunSummary } from "./syncEngine";
import type {
  OfflineOperation,
  OutboxScope,
  OutboxStore,
  OutboxSummary,
  SyncTransport,
} from "./types";

export interface PersistAndSyncResult {
  operation: OfflineOperation;
  summary: OutboxSummary;
  syncRun?: SyncRunSummary;
}

export interface OfflineCenterState {
  operations: OfflineOperation[];
  summary: OutboxSummary;
  syncRun?: SyncRunSummary;
}

function operationMatchesScope(operation: OfflineOperation, scope: OutboxScope): boolean {
  return operation.farmId === scope.farmId && operation.actorId === scope.actorId;
}

async function scopedOperation(
  store: OutboxStore,
  operationId: string,
  scope: OutboxScope,
): Promise<OfflineOperation> {
  const operation = await store.get(operationId);

  if (!operation) {
    throw new Error("SYNC_OPERATION_NOT_FOUND");
  }
  if (!operationMatchesScope(operation, scope)) {
    throw new Error("SYNC_OPERATION_SCOPE_MISMATCH");
  }

  return operation;
}

export async function readOfflineCenterState(
  store: OutboxStore,
  scope: OutboxScope,
  limit = 100,
): Promise<OfflineCenterState> {
  const [operations, summary] = await Promise.all([
    store.listActive(scope, limit),
    store.summarize(scope),
  ]);

  return { operations, summary };
}

export async function loadOfflineCenter(
  scope: OutboxScope,
  limit = 100,
): Promise<OfflineCenterState> {
  const store = await createOutboxStore();

  try {
    return await readOfflineCenterState(store, scope, limit);
  } finally {
    await store.close();
  }
}

export async function persistAndMaybeSync(
  store: OutboxStore,
  transport: SyncTransport,
  operation: OfflineOperation,
  online: boolean,
): Promise<PersistAndSyncResult> {
  const engine = new SyncEngine(store, transport);
  const scope: OutboxScope = {
    farmId: operation.farmId,
    actorId: operation.actorId,
  };
  await engine.enqueue(operation);

  const syncRun = online ? await engine.run(50, scope) : undefined;
  const stored = await store.get(operation.operationId);

  if (!stored) {
    throw new Error("SYNC_OPERATION_DISAPPEARED");
  }

  const summary = await store.summarize(scope);

  return {
    operation: stored,
    summary,
    ...(syncRun ? { syncRun } : {}),
  };
}

export async function persistAndSyncOperation(
  client: SupabaseClient,
  operation: OfflineOperation,
  online = typeof navigator === "undefined" ? true : navigator.onLine !== false,
): Promise<PersistAndSyncResult> {
  const store = await createOutboxStore();

  try {
    return await persistAndMaybeSync(store, new SupabaseSyncTransport(client), operation, online);
  } finally {
    await store.close();
  }
}

export async function reconcileReadyOperations(
  store: OutboxStore,
  transport: SyncTransport,
  scope?: OutboxScope,
): Promise<{ summary: OutboxSummary; syncRun: SyncRunSummary }> {
  const engine = new SyncEngine(store, transport);
  const syncRun = await engine.run(50, scope);
  const summary = await store.summarize(scope);
  return { summary, syncRun };
}

export async function syncPendingOperations(
  client: SupabaseClient,
  scope?: OutboxScope,
): Promise<{ summary: OutboxSummary; syncRun: SyncRunSummary }> {
  const store = await createOutboxStore();

  try {
    return await reconcileReadyOperations(store, new SupabaseSyncTransport(client), scope);
  } finally {
    await store.close();
  }
}

export async function retryOutboxOperationInStore(
  store: OutboxStore,
  transport: SyncTransport,
  operationId: string,
  scope: OutboxScope,
): Promise<OfflineCenterState> {
  const operation = await scopedOperation(store, operationId, scope);

  if (operation.status !== "retryable_error") {
    throw new Error("SYNC_OPERATION_NOT_MANUALLY_RETRYABLE");
  }

  const engine = new SyncEngine(store, transport);
  const syncRun = await engine.runOperation(operationId, scope);
  const state = await readOfflineCenterState(store, scope);

  return { ...state, syncRun };
}

export async function retryOutboxOperation(
  client: SupabaseClient,
  operationId: string,
  scope: OutboxScope,
  online = typeof navigator === "undefined" ? true : navigator.onLine !== false,
): Promise<OfflineCenterState> {
  if (!online) {
    throw new Error("SYNC_MANUAL_RETRY_OFFLINE");
  }

  const store = await createOutboxStore();

  try {
    return await retryOutboxOperationInStore(
      store,
      new SupabaseSyncTransport(client),
      operationId,
      scope,
    );
  } finally {
    await store.close();
  }
}

export async function discardTerminalConflictInStore(
  store: OutboxStore,
  operationId: string,
  scope: OutboxScope,
): Promise<OfflineCenterState> {
  const operation = await scopedOperation(store, operationId, scope);

  if (operation.status !== "terminal_conflict") {
    throw new Error("SYNC_OPERATION_NOT_TERMINAL_CONFLICT");
  }

  await store.update(operationId, {
    status: "discarded",
  });

  return readOfflineCenterState(store, scope);
}

export async function discardTerminalConflict(
  operationId: string,
  scope: OutboxScope,
): Promise<OfflineCenterState> {
  const store = await createOutboxStore();

  try {
    return await discardTerminalConflictInStore(store, operationId, scope);
  } finally {
    await store.close();
  }
}
